import React, { useMemo, useState } from "react";

import { colors } from "@material-ui/core";

import { SquareArrowOutUpRight } from "lucide-react";

import { Button } from "@/design-system/components/Button/Button";
import {
  Dialog,
  DialogActionsWrapper,
} from "@/design-system/components/Dialog/Dialog";
import { LoadingSpinner } from "@/design-system/components/LoadingSpinner/LoadingSpinner";

import { LoadedDestinationInfo } from "@/types/global";
import { DestinationConfigRow } from "@/destinations/configs/shared/ConfigPageRow";

import { TermsOfServiceState } from "@/audiences";
import { Confirmation } from "@/components/icons/svg/figma/Confirmation";

type SimpleAudiencesTermsRowProps = {
  existingConfig: any;
  destination: LoadedDestinationInfo;
  setConfigModalOpen: (modalType: string) => void;
};

function tosStateFromSettings(
  settings: Record<string, any>,
): TermsOfServiceState {
  if (
    !settings ||
    !("termsOfServiceTestState" in settings) ||
    settings["termsOfServiceTestState"] === "tosUnknown"
  ) {
    return "tosUnknown";
  }

  if (settings["termsOfServiceTestState"] === "tosTestFailed") {
    return "tosTestFailed";
  }

  if (settings["termsOfServiceTestState"] === "tosTestSucceeded") {
    return "tosTestSucceeded";
  }

  return "tosUnknown";
}

export const MicrosoftAdsAudiencesTermsOfServiceRow = ({
  existingConfig,
}: SimpleAudiencesTermsRowProps) => {
  const settings = existingConfig.settings || {};
  const idSet = "accountId" in settings && "customerId" in settings;
  const tosState = tosStateFromSettings(settings);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isVideoLoading, setIsVideoLoading] = useState(true);

  const textColor = tosState === "tosTestFailed" ? colors.red[500] : undefined;

  const microsoftAdsTOSUrl = `https://ui.ads.microsoft.com/campaign/vnext/audienceslibrary?cid=${settings.customerId}&aid=${settings.accountId}`;

  const labelText = useMemo(() => {
    switch (tosState) {
      case "tosTestFailed":
        return "Microsoft Ads' terms of service for Customer Match must be accepted before you can use this destination.";
      case "tosTestSucceeded":
        return "Microsoft Ads' terms of service for Customer Match were accepted.";
      default:
        return "Verify whether Microsoft Ads' terms of service for Customer Match have been accepted before using this destination.";
    }
  }, [tosState]);

  const button: React.ReactNode = useMemo(() => {
    if (tosState === "tosTestSucceeded") {
      return <Confirmation />;
    }

    return (
      <Button
        size="sm"
        onClick={() => {
          setIsVideoLoading(true);
          setIsModalOpen(true);
        }}
      >
        Review & Accept Terms
      </Button>
    );
  }, [tosState]);

  return (
    <>
      <DestinationConfigRow
        name="Microsoft Ads Customer Match Terms of Service"
        disabled={!idSet}
        textColor={textColor}
        labelText={labelText}
        labelStyles={{ maxWidth: 500, whiteSpace: "normal" }}
        onOpenConfigModal={() => {}}
        buttonComponent={button}
      />
      <Dialog
        open={isModalOpen}
        onOpenChange={setIsModalOpen}
        title="Review & Accept Microsoft Ads Terms"
        description={
          <>
            <p className="mb-5">
              Accepting the Terms of Service for Microsoft Ads Customer Match
              requires you to manually create a placeholder audience in the
              Microsoft Ads dashboard.
            </p>
            <ol className="mb-5 ml-6 list-decimal space-y-2 font-bold">
              <li>
                <span className="font-normal">
                  Download this sample audience CSV file:{" "}
                  <a
                    href="data:text/csv;charset=utf-8,Email%0Anot-a-real-email%40example.com"
                    download="microsoft-ads-sample.csv"
                    className="underline"
                  >
                    sample audience CSV
                  </a>
                </span>
              </li>
              <li>
                <span className="font-normal">
                  Create a Customer Match List audience in the Microsoft Ads
                  audience dashboard.
                </span>
              </li>
              <li>
                <span className="font-normal">
                  Upload the file and accept the Terms of Service when prompted
                  during the creation process.
                </span>
              </li>
            </ol>
            The following video walks you through the above steps. See the{" "}
            <a
              href="https://documentation.freshpaint.io/audiences/audience-integrations/microsoft-ads-audiences#accept-microsoftads-tos"
              target="_blank"
              rel="noopener noreferrer"
              className="underline"
            >
              Freshpaint Documentation
            </a>{" "}
            for more information.
          </>
        }
      >
        <div className="relative mt-4 h-0 bg-gray-100 pb-[64.86%]">
          {isVideoLoading && (
            <div className="absolute inset-0 flex items-center justify-center gap-2">
              <LoadingSpinner className="size-5" />
              <div className="text-gray-500">Loading video...</div>
            </div>
          )}
          <iframe
            src="https://www.loom.com/embed/056522c5338c47ba9105ee79720322e7"
            className="absolute top-0 left-0 h-full w-full"
            allow="fullscreen"
            title="Microsoft Ads Terms of Service explainer video"
            onLoad={() => setIsVideoLoading(false)}
          />
        </div>
        <DialogActionsWrapper className="mt-6">
          <Button variant="outline" onClick={() => setIsModalOpen(false)}>
            Cancel
          </Button>
          <a
            href={microsoftAdsTOSUrl}
            rel="noopener noreferrer"
            target="_blank"
            className="underline"
          >
            <Button trailingIcon={<SquareArrowOutUpRight />}>
              Go to Microsoft Ads
            </Button>
          </a>
        </DialogActionsWrapper>
      </Dialog>
    </>
  );
};
